import type { ReactElement } from 'react';
import { Navigate, Outlet } from 'react-router';
import { deriveClientAccountStatus, useMeQuery } from '@/entities/user';
import { ONBOARDING_PATH } from '@/shared/config';
import { getErrorMessage } from '@/shared/lib';
import { AuthShell } from '@/widgets/auth-shell';

/**
 * 마이페이지 입장 가드 — ACTIVE 계정만 통과시키고, 인증이 덜 된 계정은 온보딩으로 보낸다.
 *
 * 상태는 users.me 에서 파생한다(스토어의 user.status 는 온보딩 뒤에도 갱신되지 않는다).
 * RequireAuth 안쪽에서 쓰는 중첩 레이아웃 라우트다.
 */
export const RequireActive = (): ReactElement => {
  const me = useMeQuery();

  if (me.isPending) {
    return (
      <AuthShell title="마이페이지" description="상태를 확인하고 있습니다…">
        <p className="text-sm text-muted-foreground">잠시만 기다려 주세요.</p>
      </AuthShell>
    );
  }

  // 데이터가 있으면 배경 재조회 실패여도 마지막 값으로 판정한다(재조회 실패가 화면 전체를 오류 셸로 바꾸지 않게).
  if (me.data === undefined && me.isError) {
    return (
      <AuthShell title="마이페이지" description="상태를 불러오지 못했습니다.">
        <p
          role="alert"
          className="border-l-2 border-destructive bg-destructive/5 px-4 py-3 text-sm text-destructive"
        >
          {getErrorMessage(me.error, '잠시 후 다시 시도해 주세요.')}
        </p>
      </AuthShell>
    );
  }

  if (me.data === undefined || deriveClientAccountStatus(me.data) !== 'ACTIVE') {
    return <Navigate to={ONBOARDING_PATH} replace />;
  }

  return <Outlet />;
};
